import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ClickAwayListener from "@mui/material/ClickAwayListener";

/**
 * FocusDatePicker - Componente que mejora DatePicker con autoFocus y clickAway
 * @param {Object} props - Propiedades del componente
 * @param {dayjs.Dayjs|null} props.value - Valor actual de la fecha (objeto dayjs o null)
 * @param {string} props.format - Formato de la fecha (ej: "DD/MM/YYYY")
 * @param {Function} props.onChange - Función llamada al cambiar la fecha
 * @param {Function} props.onClickAway - Función llamada al hacer clic fuera del componente
 * @param {Object} props.datePickerProps - Props adicionales para el DatePicker
 */
const FocusDatePicker = ({
  value,
  format = "DD/MM/YYYY",
  onChange,
  onClickAway,
  datePickerProps = {},
}) => {
  const datePickerRef = useRef(null);

  // Efecto para dar foco automático
  useEffect(() => {
    const timer = setTimeout(() => {
      if (datePickerRef.current) {
        const inputElement = datePickerRef.current.querySelector("input");
        if (inputElement) {
          inputElement.focus();
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // Convertir valor a dayjs si es string o Date
  const dayjsValue = value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null;

  return (
    <ClickAwayListener onClickAway={() => onClickAway()}>
      <div ref={datePickerRef}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjsValue}
            format={format}
            onChange={(newValue) => {
              onChange(newValue);
            }}
            slotProps={{
              textField: {
                size: "small",
                autoFocus: true,
                fullWidth: true,
              },
              popper: {
                placement: "bottom-start",
              },
            }}
            {...datePickerProps}
          />
        </LocalizationProvider>
      </div>
    </ClickAwayListener>
  );
};

FocusDatePicker.propTypes = {
  value: PropTypes.object, // o el tipo específico que estés utilizando (Date, moment, etc.)
  format: PropTypes.string,
  onChange: PropTypes.func,
  onClickAway: PropTypes.func,
  datePickerProps: PropTypes.object,
};

export default FocusDatePicker;
